import { createFileRoute, Link } from "@tanstack/react-router";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { allPageFaqs } from "@/lib/section-faqs";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";
import { ES_TIRZEPATIDE, drugAboutAndMainEntity, drugNodes } from "@/lib/drug-schema";

const FAQS: { q: string; a: string }[] = [
  {
    q: "¿Es Zepbound más potente que Wegovy?",
    a: "En el único estudio que las comparó de forma directa (SURMOUNT-5), la tirzepatida (Zepbound) produjo mayor pérdida de peso promedio que la semaglutida (Wegovy): 20.2 por ciento frente a 13.7 por ciento del peso corporal a las 72 semanas. Las respuestas individuales varían, y factores como la cobertura del seguro, la tolerancia y los antecedentes cardiovasculares pueden hacer que cualquiera de las dos sea la mejor opción para un paciente en particular.",
  },
  {
    q: "¿Cómo consigo una receta de tirzepatida en el norte de Virginia?",
    a: "Empiece con una evaluación médica en Virginia Bariatric Surgery, en persona en Herndon o por telemedicina en cualquier parte de Virginia. Revisamos su historia clínica, sus análisis y sus metas; si la tirzepatida es apropiada, la recetamos, tramitamos la autorización previa y lo acompañamos de cerca durante el aumento de dosis.",
  },
  {
    q: "¿Recuperaré el peso si dejo Zepbound?",
    a: "En el estudio SURMOUNT-4, los pacientes que suspendieron la tirzepatida recuperaron una parte importante del peso perdido en el plazo de un año, mientras que quienes continuaron siguieron bajando. La obesidad se comporta como una enfermedad crónica, y por eso planificamos el mantenimiento desde la primera consulta: medicamento continuo, una reducción gradual o la conversación sobre opciones quirúrgicas más duraderas.",
  },
  {
    q: "¿De verdad Zepbound trata la apnea del sueño?",
    a: "Sí. Zepbound está aprobado por la FDA para la apnea obstructiva del sueño de moderada a grave en adultos con obesidad; es el primer medicamento aprobado para esa condición. En los estudios redujo notablemente los episodios de apnea, y algunos pacientes pudieron reducir o suspender el CPAP bajo supervisión médica. Si usted ronca, se despierta cansado o usa CPAP, menciónelo en su consulta: también puede fortalecer la aprobación de su seguro.",
  },
  {
    q: "¿Es segura la tirzepatida compuesta o «compounded»?",
    a: "Solo recetamos tirzepatida aprobada por la FDA (Zepbound, Mounjaro). Con la escasez resuelta, ya no se permite la producción masiva de versiones compuestas, y esos productos nunca fueron revisados por la FDA en cuanto a pureza o exactitud de la dosis. Los programas de pago propio del fabricante han reducido la diferencia de precio; pregúntenos antes de considerar una fuente no regulada.",
  },
  {
    q: "¿Puede la tirzepatida ayudar con la recuperación de peso después de una cirugía bariátrica?",
    a: "Sí. Los medicamentos de esta clase se usan cada vez más para la recuperación de peso después de una operación, y un cirujano bariátrico está en la mejor posición para manejarlo: podemos evaluar si el problema es anatómico, conductual o metabólico y tratarlo en consecuencia.",
  },
];

export const Route = createFileRoute("/es/tirzepatida")({
  head: () => {
    const base = esHead("tirzepatide");
    return {
      ...base,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              { ...esWebPageSchema("tirzepatide"), ...drugAboutAndMainEntity(ES_TIRZEPATIDE) },
              {
                "@type": "FAQPage",
                "@id": `${SITE}/es/tirzepatida#faq`,
                inLanguage: "es-US",
                // Preguntas visibles del bloque de FAQ más los encabezados
                // de sección en forma de pregunta.
                mainEntity: allPageFaqs("/es/tirzepatida", FAQS).map((f, i) => ({
                  "@type": "Question",
                  ...(i < FAQS.length ? { "@id": `${SITE}/es/tirzepatida#faq-${i + 1}` } : {}),
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
              ...drugNodes(ES_TIRZEPATIDE),
            ],
          }),
        },
      ] : [],
    };
  },
  component: EsTirzepatide,
});

function EsTirzepatide() {
  return (
    <EsPageShell
      page="tirzepatide"
      title="Tirzepatida (Zepbound® y Mounjaro®) para bajar de peso"
      intro="La tirzepatida es hoy el medicamento para bajar de peso más eficaz disponible: hasta un 21 por ciento de pérdida de peso promedio en los estudios clínicos. En Virginia Bariatric Surgery, en Herndon, la receta y la supervisa el Dr. Matthew Fitzer, cirujano bariátrico certificado. Atendemos a pacientes de todo el norte de Virginia y del área metropolitana de Washington, D.C., incluidos Herndon, Reston, Fairfax, Loudoun, Arlington, Alexandria, Sterling, Ashburn, Chantilly y Manassas, en el consultorio y por telemedicina."
    >
      <EsSection id="como-funciona" heading="Qué es la tirzepatida y cómo funciona">
        <p>
          La tirzepatida activa dos receptores hormonales a la vez: el de GLP-1 y el de GIP. Los
          medicamentos anteriores actuaban solo sobre el GLP-1. Esa acción doble reduce el apetito,
          hace más lento el vaciamiento del estómago y mejora la forma en que el cuerpo usa la
          insulina.
        </p>
        <p>
          En la práctica, esa segunda vía es la razón por la que la tirzepatida produce, en
          promedio, más pérdida de peso que cualquier otro medicamento disponible hoy.
        </p>
      </EsSection>

      <EsSection id="marcas" heading="Zepbound y Mounjaro: cuál es la diferencia">
        <p>
          Es el mismo principio activo con dos nombres comerciales. <strong>Zepbound</strong> está
          aprobado para el control crónico del peso y para la apnea obstructiva del sueño.{" "}
          <strong>Mounjaro</strong> está aprobado para la diabetes tipo 2. La diferencia importa
          sobre todo para el seguro: su plan puede cubrir uno y excluir el otro según su
          diagnóstico.
        </p>
      </EsSection>

      <EsSection id="resultados" heading="¿Cuánto peso voy a perder?">
        <p>
          En el estudio SURMOUNT-1, los adultos con obesidad perdieron en promedio entre el 15 y el
          21 por ciento de su peso corporal a las 72 semanas, según la dosis. En la dosis más alta,
          más de un tercio de los participantes perdió al menos una cuarta parte de su peso.
        </p>
        <p>
          Para una persona de 250 libras, un 21 por ciento son unas 52 libras en promedio. Los
          resultados individuales varían y dependen también de la alimentación, la actividad física
          y el tiempo que se mantenga el tratamiento.
        </p>
      </EsSection>

      <EsSection id="beneficios" heading="Beneficios más allá de la báscula">
        <p>
          Zepbound es el primer y único medicamento aprobado por la FDA para tratar la apnea
          obstructiva del sueño de moderada a grave en adultos con obesidad. En los estudios redujo
          de forma notable los episodios de apnea por hora, y algunos pacientes pudieron reducir o
          suspender el CPAP bajo supervisión médica.
        </p>
        <p>
          También mejora con frecuencia el control del azúcar en la sangre, la presión arterial, los
          lípidos y la enfermedad del hígado graso.
        </p>
      </EsSection>

      <EsSection id="candidatos" heading="¿Quién es candidato?">
        <p>
          Adultos con un IMC de 30 o más, o de 27 o más con una enfermedad relacionada con el peso.
          No es apropiada si usted o un familiar ha tenido carcinoma medular de tiroides o síndrome
          MEN 2, ni durante el embarazo. Revisamos sus antecedentes personales y familiares antes de
          recetarla.
        </p>
      </EsSection>

      <EsSection id="seguridad" heading="Efectos secundarios y por qué importa la supervisión">
        <p>
          Los efectos más comunes son digestivos: náuseas, diarrea, estreñimiento y vómito, sobre
          todo durante el aumento de dosis. Casi siempre son de leves a moderados y mejoran con el
          tiempo.
        </p>
        <p>
          Los riesgos menos frecuentes pero serios incluyen pancreatitis, enfermedad de la vesícula
          y deshidratación con daño renal. La tirzepatida lleva la misma advertencia en recuadro por
          los tumores de células C de la tiroides observados en ratas; no se sabe si ese hallazgo
          aplica a las personas.
        </p>
      </EsSection>

      <EsSection id="comparacion" heading="Tirzepatida o semaglutida: ¿cuál elegir?">
        <p>
          En el estudio SURMOUNT-5, que las comparó de forma directa, la tirzepatida produjo mayor
          pérdida de peso: 20.2 por ciento frente a 13.7 por ciento a las 72 semanas. Aun así, la{" "}
          <Link to="/es/semaglutida" className="font-medium text-brand-logo hover:underline">
            semaglutida
          </Link>{" "}
          tiene la evidencia más sólida de protección cardiovascular, y muchas veces la decisión la
          define su seguro o su tolerancia. Recetamos las dos.
        </p>
      </EsSection>

      <EsSection id="no-es-suficiente" heading="Cuando el medicamento no es suficiente">
        <p>
          No todos responden. Algunos pacientes bajan poco, otros no toleran la dosis eficaz y otros
          recuperan el peso al suspenderlo. En esos casos conversamos sobre opciones más duraderas,
          como la{" "}
          <Link to="/es/manga-gastrica" className="font-medium text-brand-logo hover:underline">
            manga gástrica
          </Link>{" "}
          o el{" "}
          <Link to="/es/bypass-gastrico" className="font-medium text-brand-logo hover:underline">
            bypass gástrico
          </Link>
          . La ventaja de tratarse aquí es que esa conversación ocurre en el mismo consultorio.
        </p>
      </EsSection>

      <EsSection id="costo" heading="Costo y cobertura en Virginia">
        <p>
          La cobertura varía mucho entre planes y casi siempre requiere autorización previa. El
          diagnóstico de apnea del sueño puede fortalecer una solicitud de Zepbound. Vea los
          requisitos plan por plan en nuestra página de{" "}
          <Link
            to="/es/cobertura-de-seguro"
            className="font-medium text-brand-logo hover:underline"
          >
            cobertura de seguro
          </Link>
          .
        </p>
      </EsSection>

      <EsSection id="preguntas" heading="Preguntas frecuentes">
        <dl className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={f.q} id={`faq-${i + 1}`}>
              <dt className="font-medium text-brand-deep">{f.q}</dt>
              <dd className="mt-1">{f.a}</dd>
            </div>
          ))}
        </dl>
      </EsSection>
    </EsPageShell>
  );
}
